import { useParams } from 'react-router';
import { useMutation } from '@apollo/client';
import { useSetRecoilState } from 'recoil';

import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';
import { ALL_ITEMS } from '@/config/common.config';
import {
  CancelContractLines, CancelContractLinesVariables
} from '@/graphql/contracts/_generated_/cancelContractLines.mutation';
import CancelContractLinesMutation from '@/graphql/contracts/cancelContractLines.mutation.gql';
import GetContractDetailsQuery from '@/graphql/contracts/getContractDetails.query.gql';
import GetPaginatedFilteredVariableContractLinesQuery from
  '@/graphql/contracts/getPaginatedFilteredVariableContractLines.query.gql';
import { rightsidePanel_isOpen } from '../../../../RightPanel.state';
import { notificationService } from '@/services/notification/notification.client';
import {
  clearLinesSelection,
  triggerLineRefresh,
  useSelectedLines
} from '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/linesSelectionObservable';
import { useSelectedVariableLines, clearVariableLinesSelection, triggerVariableRefresh } from
  '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/variableLinesSelectionObservable';
import GetBCContractLinesQuery from '@/graphql/contracts/getPaginatedFilteredBCContractLines.query.gql';

export default function useCancelContractLines() {
  const { contractId }: { contractId: UUID } = useParams();
  const selectedLineRows = useSelectedLines();
  const selectedVariableLineRows = useSelectedVariableLines();
  const selectedLines = selectedLineRows.length ? selectedLineRows : selectedVariableLineRows;
  const setIsOpenRightPanel = useSetRecoilState(rightsidePanel_isOpen);

  const [_cancelContractLines, { loading, error }] = useMutation<CancelContractLines, CancelContractLinesVariables>(
    CancelContractLinesMutation,
    {
      client: apolloClientContracts,
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetContractDetailsQuery,
          variables: { contractId }
        },
        selectedLineRows.length ?
          {
            query: GetBCContractLinesQuery,
            variables: {
              filter: {
                contractId: { eq: contractId }
              },
              first: ALL_ITEMS
            },
          } :
          {
            query: GetPaginatedFilteredVariableContractLinesQuery,
            variables: {
              filter: { headerPrimaryKey: { eq: contractId } },
              first: ALL_ITEMS,
            }
          }
      ],
      onCompleted: () => {
        clearLinesSelection();
        clearVariableLinesSelection();
        triggerLineRefresh();
        triggerVariableRefresh();
        setIsOpenRightPanel(false);
        notificationService.open({
          title: 'Contract Line Status Saved',
          text: 'The chosen contract lines have been changed',
          type: 'success'
        });
      },
      onError: (err) => notificationService.open({
        title: 'Error',
        text: err.message,
        type: 'danger'
      }),
    },
  );

  const cancelContractLines = (reasonCode: string, cancelDate: any, comment: string) => {
    _cancelContractLines({
      variables: {
        input: {
          contractId,
          cancelDate,
          comment,
          reasonCode,
          contractLines: selectedLines.map(({ id }) => ({ contractLineID: id }))
        }
      }
    });
  };

  return {
    cancelContractLines,
    isLoadingCancellation: loading,
    errorCancellation: error
  } as const;
}
